import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { fetchChatMessages } from "../services/api";
import Loader from "./Loader";
import TimeAgo from "timeago-react";
import { Card } from "react-bootstrap";

const Chat = () => {
  const SOCKET_URL =
    process.env.REACT_APP_API_SOCKET_URL || "http://148.230.125.167:9094";
  const BUSINESS_NAME = process.env.REACT_APP_BUSINESS_NAME || "Sagama";

  // Connection states: idle, connected, failed
  const [connectionStatus, setConnectionStatus] = useState("idle"); // idle | connected | failed
  const socketRef = useRef(null);

  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [currentChatPage, setCurrentChatPage] = useState(-1);
  const [totalChatPages, setTotalChatPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [showChatBox, setShowChatBox] = useState(false);
  const [showInfoForm, setShowInfoForm] = useState(true);
  const [contact, setContact] = useState({
    name: "",
    email: "",
  });
  const [chatMessages, setChatMessages] = useState([]);
  const [message, setMessage] = useState({
    messageType: "text",
    senderName: "",
    senderEmail: "",
    message: "",
  });

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Initialize socket connection and clean up
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ["websocket"],
      autoConnect: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Socket connected successfully");
      setConnectionStatus("connected");
    });

    socket.on("connect_error", (error) => {
      console.warn("Chat server unreachable – hiding chat widget.");
      setConnectionStatus("failed");
    });

    socket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      setConnectionStatus("failed");
    });

    // Cleanup on unmount
    return () => {
      socket.off("connect");
      socket.off("connect_error");
      socket.off("disconnect");
      socket.disconnect();
    };
  }, [SOCKET_URL]);

  // Handle incoming chat messages
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || connectionStatus !== "connected") return;

    const handleIncomingChat = (...incomingChatMessage) => {
      console.log("New message arrived:", incomingChatMessage);
      if (incomingChatMessage.length > 0) {
        const chatMessage = incomingChatMessage[0];
        if (
          chatMessage.senderEmail === contact.email &&
          chatMessage.senderType === "BUSINESS"
        ) {
          // Save contact info to localStorage if not already saved
          const contactDetail = localStorage.getItem("contact");
          if (!contactDetail) {
            const newContact = {
              senderName: chatMessage.senderName,
              senderEmail: chatMessage.senderEmail,
              senderDp: chatMessage.senderDp,
              recipientName: chatMessage.recipientName,
              recipientEmail: chatMessage.recipientEmail,
              recipientDp: chatMessage.recipientDp,
            };
            localStorage.setItem("contact", JSON.stringify(newContact));
          }
          setChatMessages((prev) => [...prev, chatMessage]);
          scrollToBottom();
        }
      }
    };

    socket.on("chat", handleIncomingChat);

    return () => {
      socket.off("chat", handleIncomingChat);
    };
  }, [connectionStatus, contact.email]);

  // Retrieve contact info from localStorage on mount
  useEffect(() => {
    const storedContact = localStorage.getItem("contact");
    if (storedContact) {
      try {
        const contact = JSON.parse(storedContact);
        setMessage({
          senderName: contact.senderName,
          senderEmail: contact.senderEmail,
          senderDp: contact.senderDp,
          recipientName: contact.recipientName,
          recipientEmail: contact.recipientEmail,
          recipientDp: contact.recipientDp,
        });
        setContact({
          name: contact.senderName,
          email: contact.senderEmail,
        });
        // Fetch previous chat messages
        getChatMessages(contact.senderEmail, contact.recipientEmail);
        setShowInfoForm(false);
      } catch (e) {
        console.error("Invalid contact in localStorage");
        localStorage.removeItem("contact");
      }
    }
  }, []);

  // Fetch chat history from REST API
  const getChatMessages = async (senderEmail, recipientEmail) => {
    try {
      setIsLoadingChats(true);
      const response = await fetchChatMessages(
        senderEmail,
        recipientEmail,
        currentChatPage,
        pageSize
      );
      if (response.success) {
        const data = response.payload;
        setChatMessages(data.page);
        setCurrentChatPage(data.currentPage);
        setTotalChatPages(data.totalPages);
        scrollToBottom();
      }
    } catch (error) {
      console.log("Failed to fetch chat history:", error);
    } finally {
      setIsLoadingChats(false);
    }
  };

  const sendMessage = () => {
    const socket = socketRef.current;
    if (
      !socket ||
      connectionStatus !== "connected" ||
      !message.senderName ||
      !message.senderEmail ||
      !message.message
    ) return;

    // Save contact info if not already
    if (!localStorage.getItem("contact")) {
      const contactInfo = {
        senderName: message.senderName,
        senderEmail: message.senderEmail,
        senderDp: message.senderDp,
        recipientName: message.recipientName,
        recipientEmail: message.recipientEmail,
        recipientDp: message.recipientDp,
      };
      localStorage.setItem("contact", JSON.stringify(contactInfo));
      setContact({
        name: message.senderName,
        email: message.senderEmail,
      });
    }

    socket.emit("chat", message);
    setChatMessages((prev) => [...prev, message]);
    setMessage((prev) => ({ ...prev, message: "" }));
    scrollToBottom();
  };

  const initiateChat = () => {
    if (message.senderName && message.senderEmail) {
      setContact({
        name: message.senderName,
        email: message.senderEmail,
      });
      localStorage.setItem("contact", JSON.stringify(message));
      setShowInfoForm(false);
    }
  };

  const handleScroll = (e) => {
    const element = e.target;
    if (element.scrollTop === 0 && totalChatPages > 0) {
      // Load more messages if available
      // Implementation optional – currently just placeholder
    }
  };

  // If not connected, render nothing (no trigger button)
  if (connectionStatus === "idle" || connectionStatus === "failed") {
    return null;
  }

  return (
    <Card className="card chat">
      <button
        onClick={() => setShowChatBox(!showChatBox)}
        className="play-btn chatbox-trigger-btn style3"
        aria-label="Toggle live chat"
      >
        <i className="fas fa-comments"></i>
      </button>

      {showChatBox && (
        <div className="chat-container">
          <div className="header">
            <button
              className="bg-none"
              onClick={() => setShowChatBox(false)}
            >
              <i className="fa fa-arrow-left"></i> Live Chat
            </button>
          </div>
          {showInfoForm ? (
            <div className="customer-form">
              <div className="contact-form-wrap">
                <h2 className="title h3 text-center mt-n1">Get In Touch</h2>
                <div className="form-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    name="senderName"
                    value={message.senderName}
                    onChange={(e) =>
                      setMessage({ ...message, senderName: e.target.value })
                    }
                    placeholder="Your Name*"
                    required
                  />
                  <i className="fal fa-user" />
                </div>
                <div className="form-group mb-2">
                  <input
                    type="email"
                    className="form-control"
                    name="senderEmail"
                    value={message.senderEmail}
                    onChange={(e) =>
                      setMessage({ ...message, senderEmail: e.target.value })
                    }
                    placeholder="Email Address*"
                    required
                  />
                  <i className="fal fa-envelope" />
                </div>
                <div className="form-btn col-12 mt-5">
                  <button
                    type="submit"
                    className="th-btn w-100"
                    onClick={initiateChat}
                  >
                    Continue to Chat
                    <i className="fas fa-long-arrow-right ms-2" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="frame">
              {isLoadingChats ? (
                <Loader />
              ) : (
                <ul
                  className="chat-scroller max-h-[500px]"
                  onScroll={handleScroll}
                >
                  {chatMessages.length > 0 ? (
                    <>
                      {chatMessages.map((chat, idx) => (
                        <li key={idx} className={chat.senderType === "BUSINESS" ? "w-full" : "w-fulll"}>
                          {chat.senderType === "BUSINESS" ? (
                            <div className="msj macro">
                              <div className="avatar">
                                <img
                                  className="img-circle"
                                  src="/assets/img/businessDp.png"
                                  alt=""
                                />
                              </div>
                              <div className="text text-l">
                                <b>{chat.recipientName}</b>
                                <p>{chat.message}</p>
                                <p className="datetime">
                                  <TimeAgo datetime={chat.createdAt} locale="en_US" />
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="msj-rta macro">
                              <div className="text text-r">
                                <b>You</b>
                                <p>{chat.message}</p>
                                <p className="datetime">
                                  <TimeAgo datetime={chat.createdAt} locale="en_US" />
                                </p>
                              </div>
                              <div className="avatar p-10">
                                <img
                                  className="img-circle"
                                  src="/assets/img/customerDp.png"
                                  alt=""
                                />
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  ) : (
                    <li className="w-full">
                      <div className="msj macro">
                        <div className="avatar">
                          <img
                            className="img-circle"
                            src="/assets/img/businessDp.png"
                            alt=""
                          />
                        </div>
                        <div className="text text-l">
                          <b>Mary</b>
                          <p>
                            Hello! Welcome to {BUSINESS_NAME}. I am excited to
                            help you find your dream home. How can I assist you
                            today?
                          </p>
                          <p className="datetime">
                            <small>a moment ago</small>
                          </p>
                        </div>
                      </div>
                    </li>
                  )}
                </ul>
              )}

              <div className="input-container">
                <div className="text text-r chat-input">
                  <textarea
                    value={message.message}
                    onChange={(e) =>
                      setMessage({ ...message, message: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    className="mytext"
                    rows="2"
                    placeholder="Type a message"
                  />
                </div>
                <button
                  type="button"
                  className="send-btn"
                  onClick={sendMessage}
                >
                  <span className="fa fa-send"></span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default Chat;